/* eslint-disable consistent-return */

import { check } from 'meteor/check';
import { Meteor } from 'meteor/meteor';
import { assign, filter, isEmpty, isString, last, map, toNumber } from 'lodash';
import { HTTP } from 'meteor/http';

import Connectors, { CONNECTOR_TYPES } from '../Connectors';
import * as connectorOps from '../connector-operations';

import checkAuthentication from '../../../modules/server/check-authentication';
import handleMethodException from '../../../modules/handle-method-exception';
import { isHTTPResultOK } from '../../../modules/fetch-utilites';

export const editConnectorSchema = Connectors.schema.omit(
  'owner',
  'createdAt',
  'updatedAt',
  'deleted'
);
editConnectorSchema.extend({ _id: String });

const STATUS_ERROR = 'error';
const FILE_TOO_LARGE_ERROR = 'ERROR 1: Range downloading not supported by this server!';
const DEFAULT_TABLE_VIEW_NAME = 'ningunaTabla';

const {
  checkConnectorAPI: CHECK_CONNECTOR_API,
  listConnectorSourcesAPI: LIST_CONNECTOR_SOURCES_API
} = Meteor.settings.private;

/**
 * Inserts the connector in the database.
 *
 * @async
 * @param {object} connectorObject - The connector object.
 * @param {string} userId - The user id.
 * @returns {string} Return the id of the new connector.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insert = async (connectorObject, userId) => {
  check(userId, String);
  try {
    checkAuthentication(userId);
    Connectors.schema.validate(connectorObject, {
      check,
      keys: [
        'isOutput',
        'name',
        'subtype',
        'type',
        'databaseName',
        'domain',
        'filePath',
        'fileType',
        'ip',
        'password',
        'port',
        'schema',
        'user'
      ]
    });
    return Connectors.insert({ owner: userId, ...connectorObject });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates the connector in the database.
 *
 * @param {object} connectorObject - The connector object.
 * @returns {string} Return the id of the updated connector.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const update = connectorObject => {
  try {
    checkAuthentication();
    editConnectorSchema.validate(connectorObject);
    const connectorId = connectorOps.getId(connectorObject);
    const unsetObject = {};
    if (connectorOps.hasDomain(connectorObject)) {
      unsetObject.ip = '';
      unsetObject.port = '';
    } else {
      unsetObject.domain = '';
    }
    if (connectorOps.isHTTP(connectorObject)) {
      unsetObject.user = '';
      unsetObject.password = '';
    }
    if (!connectorOps.isPostgreSQL(connectorObject)) {
      unsetObject.databaseName = '';
      unsetObject.schema = '';
    }
    if (connectorOps.isPostgreSQL(connectorObject)) {
      unsetObject.filePath = '';
      unsetObject.fileType = '';
    }

    Connectors.update(connectorId, { $set: connectorObject, $unset: unsetObject });
    return connectorId;
    // Return _id so we can redirect to connector after update.
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Copies the connector in the database.
 *
 * @param {string} connectorId - The connector id.
 * @param {string} userId - The user id that wants the connector.
 * @returns {string} Return the id of the new connector copied.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const copy = (connectorId, userId) => {
  check(connectorId, String);
  check(userId, String);
  try {
    checkAuthentication();
    const copyConnectorData = Connectors.findOne({ _id: connectorId });
    delete copyConnectorData.owner;
    delete copyConnectorData._id;
    delete copyConnectorData.createdAt;
    delete copyConnectorData.updatedAt;
    delete copyConnectorData.deleted;
    copyConnectorData.name += ' - copy';
    return Connectors.insert({ owner: userId, ...copyConnectorData });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Soft deletes the connector in the database.
 *
 * @param {string} connectorId - The connector id.
 * @returns {string} Return the id of the connector.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const softDelete = connectorId => {
  check(connectorId, String);
  try {
    checkAuthentication();
    return Connectors.update(connectorId, { $set: { deleted: new Date().toISOString() } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Restores the connector in the database.
 *
 * @param {string} connectorId - The connector id.
 * @returns {string} Return the id of the connector.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const restore = connectorId => {
  check(connectorId, String);
  try {
    checkAuthentication();
    return Connectors.update(connectorId, { $set: { deleted: 'no' } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes the connector from database.
 *
 * @param {string} connectorId - The connector id.
 * @returns {string} Return the id of the deleted connector.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const hardDelete = connectorId => {
  check(connectorId, String);
  try {
    checkAuthentication();
    return Connectors.remove(connectorId);
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Transforms a connector into post parameters format.
 *
 * @param {object} connector - The connector to transform into post parameters.
 * @returns {object} The parameters por the post.
 */
export const getLoadParametersFromConnector = connector => {
  const loadParameters = {
    protocol: connectorOps.getType(connector),
    type: connectorOps.getSubType(connector)
  };
  if (connectorOps.hasDomain(connector)) {
    assign(loadParameters, {
      domain: connectorOps.getDomain(connector),
      ip: '',
      port: ''
    });
  } else {
    assign(loadParameters, {
      ip: connectorOps.getIp(connector),
      port: toNumber(connectorOps.getPort(connector))
    });
  }
  switch (true) {
    case connectorOps.isFTP(connector):
      assign(loadParameters, {
        user: connectorOps.getUser(connector),
        password: connectorOps.getPassword(connector),
        source: connectorOps.getFilePath(connector),
        driver_type: connectorOps.getFileType(connector)
      });
      break;
    case connectorOps.isHTTP(connector):
      assign(loadParameters, {
        source: connectorOps.getFilePath(connector),
        driver_type: connectorOps.getFileType(connector),
        user: '',
        password: ''
      });
      break;
    case connectorOps.isPostgreSQL(connector):
      assign(loadParameters, {
        user: connectorOps.getUser(connector),
        password: connectorOps.getPassword(connector),
        database_name: connectorOps.getDatabaseName(connector),
        schema: connectorOps.getSchema(connector),
        driver_type: CONNECTOR_TYPES.POSTGRESQL,
        table_view_name: DEFAULT_TABLE_VIEW_NAME
      });
      break;
    default:
      break;
  }
  return loadParameters;
};

/**
 * Checks if the connector exists.
 *
 * @async
 * @param {object} connector - The connector to check.
 * @returns {object} The status of the connector.
 * @throws {Error} Meteor Error if anything goes wrong.
 */

export const checkIfValid = async connector => {
  try {
    const loadParameters = getLoadParametersFromConnector(connector);
    const response = HTTP.post(CHECK_CONNECTOR_API, {
      headers: { 'Content-Type': 'application/json' },
      data: { input_layer: { ...loadParameters }, user: Meteor.userId() }
    });
    const result = isHTTPResultOK(response);
    if (isString(result)) return { message: result };
    return result;
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Checks the connector sources.
 *
 * @async
 * @param {object} connector - The connector.
 * @param {boolean} [asTable=true] - Flag to return the list as a table.
 * @returns {object} The source list as a table object.
 * @throws {Error} - Meteor Error if anything goes wrong.
 */
export const listSources = async (connector, asTable = true) => {
  try {
    const loadParameters = getLoadParametersFromConnector(connector);
    const response = HTTP.post(LIST_CONNECTOR_SOURCES_API, {
      headers: { 'Content-Type': 'application/json' },
      data: { input_layer: { ...loadParameters }, user: Meteor.userId() }
    });
    const result = isHTTPResultOK(response);
    if (result.status === STATUS_ERROR) throw new Error(result.message);
    if (result.message === FILE_TOO_LARGE_ERROR) throw new Error(result.message);
    if (isString(result)) throw new Error(result);

    if (connectorOps.isOutput(connector)) return [];

    const rawCSV = result.message;
    const rowArray = rawCSV.split('\n');
    const tableTitle = map(rowArray[0].split(','), title => title.trim());
    const fullBodyArray = filter(rowArray.slice(1), row => !isEmpty(row));
    const tableBody = map(fullBodyArray, row => row.trim().split(','));
    if (asTable) return { tableBody, tableTitle };

    let sources = tableBody;
    if (connectorOps.getType(connector) === CONNECTOR_TYPES.POSTGRESQL)
      sources = map(
        filter(tableBody, row => row[0] === connectorOps.getSchema(connector)),
        row => last(row)
      );
    return sources;
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Find one connectors in database.
 *
 * @param {string} connectorId - The annotation property for filter.
 */
export const findOne = connectorId => {
  check(connectorId, String);
  try {
    checkAuthentication();
    const connector = Connectors.findOne({ _id: connectorId });
    return connector;
  } catch (exception) {
    handleMethodException(exception);
  }
};
